/** Visão geral › Vendas por anúncio (14/10/2026): uma linha por anúncio × variação dos SKUs de Natal, com filtro de
 * data, título, SKU e canal. Números em `vendasPorVariacao` (logic.ts) a partir de `vendas_dia_anuncio` (serviço);
 * pedido com vários anúncios já vem separado — nunca junta produtos numa linha. Clique na linha abre os pedidos. */
import { useMemo, useState } from 'react'
import {
  Autocomplete, Box, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, Switch, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material'
import { Comps, Mc, Produto, TabelaOrdenavel, type Coluna } from './componentes'
import { Pedidos } from './FaturamentoNiveis'
import { brl, brl2, dataBr, nomeLoja, num, pct, totalVendas, vendasPorVariacao, type FiltroVendas, type LinhaVendaAnuncio, type Somas } from './logic'
import type { NatalData } from './types'

type Atalho = '7d' | '14d' | 'temp' | null

function Delta({ v, ant, dinheiro }: { v: number; ant: number | null | undefined; dinheiro?: boolean }) {
  if (ant == null) return <Typography component="span" variant="caption" color="text.secondary" title="O período anterior cai antes do início da temporada">—</Typography>
  if (!ant) return v ? <Typography component="span" variant="caption" color="success.main">novo</Typography> : <>—</>
  const d = v / ant - 1
  return (
    <Typography component="span" variant="caption" color={d >= 0 ? 'success.main' : 'error.main'} title={`período anterior: ${dinheiro ? brl(ant) : num(ant)}`}>
      {d >= 0 ? '+' : ''}{pct(d)}
    </Typography>
  )
}

function Pend({ s }: { s: Somas }) {
  if (s.pend < 0.5) return null
  return <Typography variant="caption" color="warning.main" display="block" title="Pedidos pagos ainda sem margem oficial (envio pendente ou retido pelo marketplace): valor pago e MC estimada">inclui {brl(s.pend)} margem pendente</Typography>
}

export function VendasPorAnuncio({ d }: { d: NatalData }) {
  const cal = d.calendario
  const ih = Math.max(0, Math.min(d.i_hoje, cal.length - 1))
  const desde = (n: number) => cal[Math.max(0, ih - n + 1)]
  const [f, setF] = useState<FiltroVendas>({ de: desde(7), ate: cal[ih], titulo: '', skus: [], lojas: [], semVenda: false })
  const [atalho, setAtalho] = useState<Atalho>('7d')
  const [aberta, setAberta] = useState<LinhaVendaAnuncio | null>(null)
  const linhas = useMemo(() => vendasPorVariacao(d, f), [d, f])
  const tot = useMemo(() => totalVendas(linhas), [linhas])
  const lojas = useMemo(() => [...new Set(d.anuncios.map((a) => a.loja))], [d])
  const skus = useMemo(() => d.skus.map((k) => k.sku).sort(), [d])
  const semana = d.i_hoje >= 6 && f.ate === cal[d.i_hoje] && f.de === cal[d.i_hoje - 6]

  const usarAtalho = (a: Atalho) => {
    if (!a) return
    setAtalho(a)
    setF({ ...f, de: a === 'temp' ? cal[0] : desde(a === '7d' ? 7 : 14), ate: cal[ih] })
  }
  const data = (campo: 'de' | 'ate', v: string) => {
    if (!cal.includes(v)) return
    setAtalho(null)
    setF({ ...f, [campo]: v, ...(campo === 'de' && v > f.ate ? { ate: v } : {}), ...(campo === 'ate' && v < f.de ? { de: v } : {}) })
  }

  const colunas: Coluna<LinhaVendaAnuncio>[] = [
    { label: 'Anúncio', valor: (l) => l.titulo, render: (l) => (
      <Produto largura={360} a={{ titulo: l.titulo, link: l.link, imagem: l.imagem, id: l.id, item_id: l.id, variacao: l.nomeVariacao, sku_antigo: null }}
        extra={<Typography variant="caption" color="text.secondary" display="block">
          {nomeLoja(d, l.loja)}{l.full ? ' · Full' : ''}{l.status === 'pausado' ? ' · pausado' : l.status === 'outro' ? ' · outro status' : l.status === 'nao_identificada' ? ' · sem variação identificada' : ''}
        </Typography>} />) },
    { label: 'SKU', valor: (l) => l.comps.map(([s]) => s).join(','), render: (l) => <Comps comps={l.comps} /> },
    { label: 'Pedidos', valor: (l) => l.atual.pedidos, render: (l) => num(l.atual.pedidos), alinhar: 'right' },
    { label: 'Unidades', valor: (l) => l.atual.un, render: (l) => <>{num(l.atual.un)} <Delta v={l.atual.un} ant={l.anterior?.un} /></>, alinhar: 'right' },
    { label: 'Peças', valor: (l) => l.atual.pecas, render: (l) => num(l.atual.pecas), alinhar: 'right' },
    { label: 'Faturamento', valor: (l) => l.atual.fat, render: (l) => <Box>{brl(l.atual.fat)} <Delta v={l.atual.fat} ant={l.anterior?.fat} dinheiro /><Pend s={l.atual} /></Box>, alinhar: 'right' },
    { label: 'Preço médio', valor: (l) => (l.atual.un ? l.atual.fat / l.atual.un : null), render: (l) => (l.atual.un ? brl2(l.atual.fat / l.atual.un) : '—'), alinhar: 'right' },
    { label: 'MC', valor: (l) => l.atual.mc, render: (l) => brl(l.atual.mc), alinhar: 'right' },
    { label: 'MC %', valor: (l) => (l.atual.fat ? l.atual.mc / l.atual.fat : null), render: (l) => <><Mc v={l.atual.fat ? l.atual.mc / l.atual.fat : null} />{l.atual.pend >= 0.5 ? ' est.' : ''}</>, alinhar: 'right' },
    { label: 'Preço atual', valor: (l) => l.preco, render: (l) => brl2(l.preco), alinhar: 'right' },
    { label: 'Estoque', valor: (l) => l.estoque, render: (l) => num(l.estoque), alinhar: 'right' },
    { label: 'Visitas 7d', valor: (l) => l.visitas, render: (l) => num(l.visitas), alinhar: 'right' },
    { label: 'Conversão 7d', valor: (l) => l.conversao, render: (l) => (l.conversao == null
      ? <Typography component="span" variant="caption" color="text.secondary" title={semana ? 'Sem visitas lidas (só o ML dá visitas ao painel)' : 'Só com o filtro de data nos últimos 7 dias (as visitas são dos últimos 7 dias)'}>—</Typography>
      : pct(l.conversao, 1)), alinhar: 'right' },
  ]

  return (
    <Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ my: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={atalho} onChange={(_, v: Atalho) => usarAtalho(v)}>
          <ToggleButton value="7d">7 dias</ToggleButton><ToggleButton value="14d">14 dias</ToggleButton><ToggleButton value="temp">Temporada</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" type="date" label="De" value={f.de} onChange={(e) => data('de', e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: cal[0], max: cal[ih] } }} />
        <TextField size="small" type="date" label="Até" value={f.ate} onChange={(e) => data('ate', e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: cal[0], max: cal[ih] } }} />
        <TextField size="small" label="Título, anúncio ou variação" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} sx={{ minWidth: 220 }} />
        <Autocomplete multiple size="small" options={skus} value={f.skus} onChange={(_, v) => setF({ ...f, skus: v })} sx={{ minWidth: 220 }}
          renderInput={(p) => <TextField {...p} label="SKU" />} />
        <Autocomplete multiple size="small" options={lojas} value={f.lojas} onChange={(_, v) => setF({ ...f, lojas: v })} sx={{ minWidth: 220 }}
          getOptionLabel={(k) => nomeLoja(d, k)} renderInput={(p) => <TextField {...p} label="Canal" />} />
        <FormControlLabel control={<Switch size="small" checked={f.semVenda} onChange={(e) => setF({ ...f, semVenda: e.target.checked })} />} label="Incluir anúncios sem venda" />
      </Stack>
      {!d.vendas_dia_anuncio && <Typography variant="body2" color="text.secondary">As vendas por anúncio aparecem depois da próxima atualização do painel.</Typography>}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="body2">{dataBr(f.de)} a {dataBr(f.ate)} · <b>{num(linhas.length)}</b> anúncios/variações</Typography>
        <Typography variant="body2">Pedidos <b>{num(tot.atual.pedidos)}</b></Typography>
        <Typography variant="body2">Unidades <b>{num(tot.atual.un)}</b> <Delta v={tot.atual.un} ant={tot.anterior?.un} /></Typography>
        <Typography variant="body2">Faturamento <b>{brl(tot.atual.fat)}</b> <Delta v={tot.atual.fat} ant={tot.anterior?.fat} dinheiro /></Typography>
        <Typography variant="body2">MC <b>{brl(tot.atual.mc)}</b> (<Mc v={tot.atual.fat ? tot.atual.mc / tot.atual.fat : null} />)</Typography>
        {tot.atual.pend >= 0.5 && <Chip size="small" color="warning" variant="outlined" label={`inclui ${brl(tot.atual.pend)} margem pendente`} />}
      </Stack>
      <TabelaOrdenavel vazio="Nenhum anúncio com venda no filtro." linhas={linhas} colunas={colunas} onLinha={(l) => setAberta(l)} />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Uma linha por anúncio e variação; pedido com mais de um anúncio é separado e cada anúncio recebe a sua parte. Kit = um anúncio. Pedidos e unidades do anúncio;
        peças = unidades × peças do kit. Faturamento e MC = margem oficial por pedido (rateada pelo custo entre os SKUs do pedido); pedido pago ainda sem margem entra com MC estimada.
        Variação contra o período anterior de mesmo tamanho. Clique na linha para ver os pedidos.
      </Typography>
      <Dialog open={!!aberta} onClose={() => setAberta(null)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {aberta?.titulo}{aberta?.nomeVariacao ? ` · ${aberta.nomeVariacao}` : ''}
          <Typography variant="caption" color="text.secondary" display="block">{aberta ? `${nomeLoja(d, aberta.loja)} · ${aberta.id}` : ''}</Typography>
          <IconButton aria-label="fechar" onClick={() => setAberta(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>×</IconButton>
        </DialogTitle>
        <DialogContent>
          {aberta && <Pedidos loja={aberta.loja} anuncio={aberta.anuncio} skus={aberta.comps.map(([s]) => s)}
            recorte={{ de: f.de, ate: f.ate, variacao: aberta.variacao }} />}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
